/**
 * ============================================================
 * sentry.ts — Rastreamento de Erros com Sentry
 * ============================================================
 *
 * Responsabilidade: Inicializar o Sentry para captura de erros,
 *   rejeicoes nao tratadas e traces de performance em producao.
 *
 * Configuracao:
 *   - Requer VITE_SENTRY_DSN no .env
 *   - Em DEV: desabilitado (nao inicializa)
 *   - Em staging: traces 100%, replay 50%
 *   - Em producao: traces 20%, replay 5% (100% em erros)
 *
 * LGPD: Apenas user.id e enviado ao Sentry (sem email/PII).
 *
 * Quando usar: Chamar initSentry() no main.tsx.
 *   Usar captureError() para erros manuais com contexto.
 *   Usar setSentryUser() apos login/logout.
 * ============================================================
 */
import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const IS_STAGING = import.meta.env.VITE_STAGING_MODE === 'true';
const IS_DEV = import.meta.env.DEV;

/** Inicializa Sentry com config de ambiente. No-op em DEV ou sem DSN. */
export function initSentry() {
  if (!SENTRY_DSN || IS_DEV) {
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: IS_STAGING ? 'staging' : 'production',
    release: `legacy-app@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,

    // Performance monitoring
    tracesSampleRate: IS_STAGING ? 1.0 : 0.2,

    // Session replay (reduced in prod)
    replaysSessionSampleRate: IS_STAGING ? 0.5 : 0.05,
    replaysOnErrorSampleRate: 1.0,

    // Filter noisy errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection',
      'AbortError',
      /Loading chunk .* failed/,
    ],

    beforeSend(event) {
      // Don't send events from local development
      if (window.location.hostname === 'localhost') return null;
      return event;
    },
  });
}

/** Captura erro customizado com contexto adicional no Sentry */
export function captureError(error: Error, context?: Record<string, unknown>) {
  if (!SENTRY_DSN) return;

  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(error);
  });
}

export { Sentry };
